/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { Box } from '@mui/material';
import useTableSort from '../../hooks/useTableSort';
import SortableTableHeader from '../shared/SortableTableHeader';
import { toast } from 'react-toastify';
import '../../styles/DepartmentManagement.css';

const DepartmentManagement = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [newDepartment, setNewDepartment] = useState({
    department_name: '',
    department_code: '',
    ouc_code: ''
  });

  // Filter departments by search term
  const filteredDepartments = departments.filter(dept => {
    return searchTerm === '' ||
      dept.department_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dept.department_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dept.ouc_code?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Sorting hook
  const { sortedData, sortColumn, sortOrder, handleSort, resetSort } = useTableSort(
    filteredDepartments,
    { defaultSortColumn: 'department_name', defaultSortOrder: 'asc' }
  );

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      const response = await axios.get('/api/departments');
      setDepartments(response.data);
      setLoading(false);
    } catch (error) {
      setError('Failed to fetch departments');
      setLoading(false);
    }
  };

  const handleEditClick = (department) => {
    setEditingDepartment(department);
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    try {
      await axios.put(`/api/departments/${editingDepartment._id}`, editingDepartment);
      setEditDialogOpen(false);
      setEditingDepartment(null);
      fetchDepartments();
    } catch (error) {
      console.error('Failed to update department:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this department?')) {
      try {
        await axios.delete(`/api/departments/${id}`);
        fetchDepartments();
      } catch (error) {
        console.error('Failed to delete department:', error);
      }
    }
  };

  const validateDepartment = (department) => {
    const errors = [];
    
    if (!department.department_name?.trim()) {
      errors.push('Department name is required');
    }

    // Validate department code (required, no lowercase letters allowed)
    if (!department.department_code?.trim()) {
      errors.push('Department code is required');
    } else if (/[a-z]/.test(department.department_code)) {
      errors.push('Department code cannot contain lowercase letters');
    }

    // Validate OUC code (6 digits)
    if (!/^\d{6}$/.test(department.ouc_code)) {
      errors.push('OUC code must be exactly 6 digits');
    }

    return errors;
  };

  const handleCreate = async () => {
    try {
      const errors = validateDepartment(newDepartment);
      if (errors.length > 0) {
        alert(errors.join('\n'));
        return;
      }

      await axios.post('/api/departments', newDepartment);
      setNewDepartment({
        department_name: '',
        department_code: '',
        ouc_code: ''
      });
      fetchDepartments();
      toast.success('Department created successfully');
    } catch (error) {
      console.error('Failed to create department:', error);
      toast.error(error.response?.data?.error || 'Failed to create department');
    }
  };

  const renderEditForm = () => (
    <Dialog 
      open={editDialogOpen} 
      onClose={() => setEditDialogOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Edit Department</DialogTitle>
      <DialogContent>
        <div className="edit-form">
          <TextField
            label="Department Name"
            fullWidth
            margin="normal"
            value={editingDepartment?.department_name || ''}
            onChange={(e) => setEditingDepartment({
              ...editingDepartment,
              department_name: e.target.value
            })}
          />
          <TextField
            label="Department Code"
            fullWidth
            margin="normal"
            value={editingDepartment?.department_code || ''}
            onChange={(e) => {
              // Convert lowercase to uppercase, allow special characters
              const value = e.target.value.replace(/[a-z]/g, char => char.toUpperCase());
              setEditingDepartment({
                ...editingDepartment,
                department_code: value
              });
            }}
            helperText="Uppercase letters, numbers, and special characters allowed (no lowercase)"
          />
          <TextField
            label="OUC Code"
            fullWidth
            margin="normal"
            value={editingDepartment?.ouc_code || ''}
            onChange={(e) => setEditingDepartment({
              ...editingDepartment,
              ouc_code: e.target.value.replace(/\D/g, '').slice(0, 6)
            })}
            inputProps={{ maxLength: 6 }}
            helperText="Must be exactly 6 digits"
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleEditSave} variant="contained" color="primary">
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderCreateForm = () => (
    <Dialog 
      open={createDialogOpen} 
      onClose={() => setCreateDialogOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Create New Department</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          margin="normal"
          label="Department Name"
          value={newDepartment.department_name}
          onChange={(e) => setNewDepartment({
            ...newDepartment,
            department_name: e.target.value
          })}
          required
        />
        <TextField
          fullWidth
          margin="normal"
          label="Department Code"
          value={newDepartment.department_code}
          onChange={(e) => {
            // Convert lowercase to uppercase, allow special characters
            const value = e.target.value.replace(/[a-z]/g, char => char.toUpperCase());
            setNewDepartment({
              ...newDepartment,
              department_code: value
            });
          }}
          required
          helperText="Uppercase letters, numbers, and special characters allowed (no lowercase)"
        />
        <TextField
          fullWidth
          margin="normal"
          label="OUC Code"
          value={newDepartment.ouc_code}
          onChange={(e) => setNewDepartment({
            ...newDepartment,
            ouc_code: e.target.value.replace(/\D/g, '').slice(0, 6)
          })}
          inputProps={{ maxLength: 6 }}
          required
          helperText="Must be exactly 6 digits"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleCreate} variant="contained" color="primary">
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="department-management">
      <h2>Department Management</h2>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
        <TextField
          label="Search Departments"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name, code..."
          sx={{ minWidth: 300 }}
        />
      </Box>

      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setCreateDialogOpen(true)}
        >
          Add New Department
        </Button>
        <Button
          variant="outlined"
          onClick={resetSort}
        >
          Reset Sort
        </Button>
      </Box>

      <table className="data-table">
        <thead>
          <tr>
            <SortableTableHeader 
              column="department_name" 
              label="Department Name" 
              currentSortColumn={sortColumn}
              sortOrder={sortOrder}
              onSort={handleSort}
            />
            <SortableTableHeader 
              column="department_code" 
              label="Department Code" 
              currentSortColumn={sortColumn}
              sortOrder={sortOrder}
              onSort={handleSort}
            />
            <SortableTableHeader 
              column="ouc_code" 
              label="OUC Code" 
              currentSortColumn={sortColumn}
              sortOrder={sortOrder}
              onSort={handleSort}
            />
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map(department => (
            <tr key={department._id}>
              <td>{department.department_name}</td>
              <td>{department.department_code}</td>
              <td>{department.ouc_code}</td>
              <td className="action-buttons">
                <Button
                  variant="contained"
                  size="small"
                  className="row-action-edit"
                  style={{ marginRight: '8px' }}
                  onClick={() => handleEditClick(department)}
                >
                  Edit
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="error"
                  onClick={() => handleDelete(department._id)}
                >
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {renderEditForm()}
      {renderCreateForm()}
    </div>
  );
};

export default DepartmentManagement;